import { styled } from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import React, { FC } from 'react';
import { useAtomValue } from 'jotai';
import {
  useBreakPoint,
  hostname,
  Styling,
  ResponsiveImage,
  Divider,
  Box,
  useDialogs,
  MobileProfileMenu,
  ProfileMenu,
} from '@idk-web/core-ui';
import { Language } from '@idk-web/api';
import { inboxLoginStatus, inboxUser } from '@/state/inboxAuth';
import { useInboxLogin } from '@/hooks/inbox/login/useInboxLogin';
import routes from '@/components/inbox/routes';
import LanguageSelect from '@/components/common/input/LanguageSelect';
import LogoutDialog from '@/components/common/dialog/LogoutDialog';
import idkollen from '@/assets/svg/idkollen.svg';
import identisure from '@/assets/svg/identisure.svg';

const Container = styled.header`
  position: relative;
  display: grid;
  grid-template-columns: auto auto;
  justify-content: space-between;
  min-height: 80px;
  z-index: ${Styling.zIndex('header')};
  box-shadow: ${Styling.shadow('header')};
  background-color: ${({ theme }) => theme.palette.grayscale.white};
  padding: 0 ${Styling.spacing(8)};
  @media (max-width: ${Styling.breakpoint('lg')}px) {
    padding: 0 ${Styling.spacing(4)};
  }
  @media (max-width: ${Styling.breakpoint('sm')}px) {
    padding: 0 ${Styling.spacing(2)};
  }
`;

const Section = styled(Box).attrs(({ theme }) => ({
  alignX: 'center',
  alignY: 'center',
  spacing: theme.spacing[2],
}))``;

const Logo = styled(ResponsiveImage).attrs({
  alt: 'Logo', // TODO translate
})`
  max-width: 160px;
  max-height: 120px;
  padding: ${Styling.spacing(2)} 0;
`;

const ShortDivider = styled(Divider)`
  height: 16px;
`;

export type HeaderProps = {
  logo?: string;
  languages?: Language[];
};

const Header: FC<HeaderProps> = ({ logo, languages }) => {
  const { t } = useTranslation();
  const dialog = useDialogs();
  const navigate = useNavigate();
  const breakpoint = useBreakPoint('xl');
  const auth = useInboxLogin();
  const user = useAtomValue(inboxUser);
  const loginStatus = useAtomValue(inboxLoginStatus);
  const isLoggedIn = loginStatus === 'LOGGED_IN';

  const handleGoToProfile = () => navigate(routes.profile);

  const handleLogout = () => {
    const handleOk = () => auth.logout();

    dialog.open((props) => <LogoutDialog {...props} onOk={handleOk} />);
  };

  const menuItems = [
    {
      content: t('inbox.header.menu.profile.item'),
      onClick: handleGoToProfile,
    },
    'divider' as const,
    {
      content: t('inbox.header.menu.log_out.item'),
      onClick: handleLogout,
    },
  ];

  return (
    <Container>
      <Link to={routes.inbox}>
        {logo ? (
          <Logo src={logo} />
        ) : (
          <Logo src={hostname.tld === 'se' ? idkollen : identisure} />
        )}
      </Link>
      <Section>
        <LanguageSelect
          availableLanguages={
            languages && languages.length !== 0
              ? languages
              : Object.values(Language)
          }
        />
        {isLoggedIn && (
          <>
            <ShortDivider orientation="vertical" />
            {breakpoint.below ? (
              <MobileProfileMenu items={menuItems} />
            ) : (
              <ProfileMenu
                username={user?.displayName ?? user?.email ?? 'Anonymous'}
                items={menuItems}
              />
            )}
          </>
        )}
      </Section>
    </Container>
  );
};

export default Header;
